import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import type { PmmSession } from '../domain/auth'
import type { CandidatePublicationRequest, CandidateReviewRequest, CandidateRevisionRequest, CoachCandidateBundle, CoachProgressMemoryView, CoachProgressRunProjection, CoachReadinessPacket, RelationshipRunProjection } from '../domain/coach'
import { isConsentGranted, type ConsentDecisionRequest, type ConsentState } from '../domain/consent'
import type { DevicePrivacySettings, PrivacyJob, PrivacySnapshot } from '../domain/privacy'
import type { PracticeLibraryView, PracticePreferenceRequest, SaveWeeklyCouncilRequest, SaveWeeklyCouncilResult, WeeklyCouncilView } from '../domain/weekly'
import {
  ONBOARDING_SCHEMA_VERSION,
  type OnboardingChapterSaveRequest,
  type OnboardingRestartRequest,
  type OnboardingRestartResult,
  type OnboardingResponse,
  type OnboardingSession,
  type OnboardingSubmissionResult,
} from '../domain/onboarding'
import type { ClientAssetProjection } from '../domain/types'
import type { DailyExperienceRequest, DailyExperienceResult, DailyInteractionRequest, DailyInteractionResult, DailySwapRequest } from '../domain/daily'
import type { AssetResponse, DailyPlan } from '../domain/dailyEngine'
import type { FoundationCapacity, FoundationInteractionRequest, FoundationInteractionResult, FoundationView } from '../domain/foundation'
import type { ProfileFactCorrectionRequest, RelationshipIntelligenceBundle, SpineHypothesisReviewRequest } from '../domain/intelligence'
import type { NotificationSettingsUpdate, NotificationSettingsView, NotificationTestResult, PushInstallationRegistration } from '../domain/notifications'
import type { OfflineSyncReport } from '../domain/offline'
import { cacheOfflineToday, clearAllPmmSensitiveDeviceState, clearEncryptedDeviceVault, queueOfflineInteraction, readOfflineQueue, readOfflineToday, replaceOfflineQueue } from '../offline/encryptedVault'
import { ensurePmmAppCheckReady, getPmmFirebaseServices } from '../lib/firebase/client'
import type { PmmRepository } from './repository'

interface ConsentResult { state: ConsentState }
interface PrivacyJobResult { job: PrivacyJob }
interface OnboardingSaveResult { session: OnboardingSession }
interface OnboardingSubmitResult extends OnboardingSubmissionResult {}
interface IntelligenceResult { bundle: RelationshipIntelligenceBundle }
interface CoachReadinessResult { readiness: CoachReadinessPacket }
interface CoachProgressRunResult { run: CoachProgressRunProjection }
interface RelationshipRunResult { run: RelationshipRunProjection }
interface CandidateSetResult extends CoachCandidateBundle {}
interface PublicationResult { publishedCount: number }

function isOfflineFailure(error: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true
  const code = String((error as { code?: unknown })?.code ?? '').toLowerCase()
  return ['unavailable', 'deadline-exceeded', 'network-request-failed', 'internal'].some((value) => code.includes(value))
}

function safeErrorCode(error: unknown): string {
  return String((error as { code?: unknown })?.code ?? 'sync_rejected').replace(/[^a-z0-9/_-]/gi, '').slice(0, 80) || 'sync_rejected'
}
export class FirestorePmmRepository implements PmmRepository {
  readonly mode = 'firestore' as const

  constructor(private readonly session: PmmSession) {}

  private privacySnapshot?: PrivacySnapshot

  private async offlineEnabled(): Promise<boolean> {
    const snapshot = this.privacySnapshot ?? await this.getPrivacySnapshot().catch(() => undefined)
    return snapshot?.device.trustedDevice === true
      && snapshot.device.persistentCacheActive === true
      && isConsentGranted(snapshot.consent, 'offline_persistent_cache')
  }
  async getDailyExperience(request: DailyExperienceRequest): Promise<DailyPlan> {
    try {
      const { functions } = await getPmmFirebaseServices()
      const call = httpsCallable<DailyExperienceRequest, DailyExperienceResult>(functions, 'getOrCreatePmmDailyExperience')
      const plan = { ...(await call(request)).data.plan, deliveryState: 'live' as const }
      if (plan.availability === 'waiting_for_coach') {
        await clearEncryptedDeviceVault().catch(() => undefined)
      } else if (await this.offlineEnabled()) {
        await cacheOfflineToday(this.session.uid, plan).catch(() => undefined)
      }
      return plan
    } catch (error) {
      if (isOfflineFailure(error) && await this.offlineEnabled()) {
        const cached = await readOfflineToday(this.session.uid, request.localDate).catch(() => null)
        if (cached && !cached.contentPackVersion.startsWith('pmm-starter-')) return cached
      }
      throw error
    }
  }

  async regenerateDailyAsset(request: DailySwapRequest): Promise<DailyPlan> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<DailySwapRequest, DailyExperienceResult>(functions, 'regeneratePmmDailyAsset')
    return (await call(request)).data.plan
  }

  private async recordDailyInteractionOnline(request: DailyInteractionRequest): Promise<DailyInteractionResult> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<DailyInteractionRequest, DailyInteractionResult>(functions, 'recordPmmAssetInteraction')
    return (await call(request)).data
  }

  async recordDailyInteraction(request: DailyInteractionRequest): Promise<DailyInteractionResult> {
    try {
      return await this.recordDailyInteractionOnline(request)
    } catch (error) {
      if (!isOfflineFailure(error) || !await this.offlineEnabled()) throw error
      const pendingCount = await queueOfflineInteraction(this.session.uid, request)
      return { interactionId: `queued:${request.clientMutationId}`, occurredAt: new Date().toISOString(), queued: true, pendingCount }
    }
  }

  async getPracticeLibrary(): Promise<PracticeLibraryView> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<Record<string, never>, PracticeLibraryView>(functions, 'getPmmPracticeLibrary')
    return (await call({})).data
  }

  async updatePracticePreference(request: PracticePreferenceRequest): Promise<PracticeLibraryView> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<PracticePreferenceRequest, PracticeLibraryView>(functions, 'updatePmmPracticePreference')
    return (await call(request)).data
  }

  async getWeeklyCouncil(weekId: string): Promise<WeeklyCouncilView> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<{ weekId: string }, { view: WeeklyCouncilView }>(functions, 'getPmmWeeklyCouncil')
    return (await call({ weekId })).data.view
  }

  async saveWeeklyCouncil(request: SaveWeeklyCouncilRequest): Promise<SaveWeeklyCouncilResult> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<SaveWeeklyCouncilRequest, SaveWeeklyCouncilResult>(functions, 'savePmmWeeklyCouncil')
    return (await call(request)).data
  }

  async getFoundationJourney(capacity: FoundationCapacity): Promise<FoundationView> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<{ capacity: FoundationCapacity }, { view: FoundationView }>(functions, 'getPmmFoundationJourney')
    return (await call({ capacity })).data.view
  }

  async recordFoundationInteraction(request: FoundationInteractionRequest): Promise<FoundationInteractionResult> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<FoundationInteractionRequest, FoundationInteractionResult>(functions, 'recordPmmFoundationInteraction')
    return (await call(request)).data
  }

  async saveResponse(response: AssetResponse): Promise<void> {
    if (!response.planId || !response.localDate) throw new Error('Daily plan context is required.')
    await this.recordDailyInteraction({
      assetId: response.assetId, planId: response.planId, localDate: response.localDate,
      action: response.state, value: response.value, feedback: response.feedback,
      clientMutationId: `legacy-${response.assetId}-${Date.now()}`,
    })
  }

  async publishClientAssets(_assets: ClientAssetProjection[]): Promise<void> {
    throw new Error('secure_candidate_publication_requires_loop06')
  }

  async listPublishedClientAssets(): Promise<ClientAssetProjection[]> {
    const { firestore } = await getPmmFirebaseServices()
    const snapshot = await getDocs(query(
      collection(firestore, 'pmm_client_asset_projections'),
      where('ownerUid', '==', this.session.uid),
      where('state', '==', 'published')
    ))
    return snapshot.docs.map((item) => {
      const data = item.data() as ClientAssetProjection
      return { ...data, assetId: item.id }
    })
  }

  async getCoachRelationshipReadiness(): Promise<CoachReadinessPacket> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<Record<string, never>, CoachReadinessResult>(functions, 'getPmmCoachRelationshipReadiness')
    return (await call({})).data.readiness
  }

  async getCoachProgressMemory(): Promise<CoachProgressMemoryView> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<Record<string, never>, CoachProgressMemoryView>(functions, 'getPmmCoachProgressMemory')
    return (await call({})).data
  }

  async startCoachProgressMemory(clientMutationId: string): Promise<CoachProgressRunProjection> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<{ clientMutationId: string }, CoachProgressRunResult>(functions, 'startPmmCoachProgressMemory')
    return (await call({ clientMutationId })).data.run
  }

  async startRelationshipExpansion(clientMutationId: string, generationMode: 'standard' | 'full_library_refresh' = 'standard'): Promise<RelationshipRunProjection> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<{ clientMutationId: string; generationMode: 'standard' | 'full_library_refresh' }, RelationshipRunResult>(functions, 'startPmmRelationshipExpansion')
    return (await call({ clientMutationId, generationMode })).data.run
  }

  async getRelationshipRunStatus(runId: string): Promise<RelationshipRunProjection> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<{ runId: string }, RelationshipRunResult>(functions, 'getPmmRelationshipRunStatus')
    return (await call({ runId })).data.run
  }

  async getCoachCandidateSet(candidateSetId: string): Promise<CoachCandidateBundle> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<{ candidateSetId: string }, CandidateSetResult>(functions, 'getPmmCoachCandidateSet')
    return (await call({ candidateSetId })).data
  }

  async saveCoachCandidateRevision(request: CandidateRevisionRequest): Promise<void> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<CandidateRevisionRequest, unknown>(functions, 'savePmmCoachCandidateRevision')
    await call(request)
  }

  async reviewCoachCandidate(request: CandidateReviewRequest): Promise<void> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<CandidateReviewRequest, unknown>(functions, 'reviewPmmCoachCandidate')
    await call(request)
  }

  async publishCoachCandidateSet(request: CandidatePublicationRequest): Promise<{ publishedCount: number }> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<CandidatePublicationRequest, PublicationResult>(functions, 'publishPmmCoachCandidateSet')
    return (await call(request)).data
  }

  async getPrivacySnapshot(): Promise<PrivacySnapshot> {
    const { firestore } = await getPmmFirebaseServices()
    const [consentDoc, settingsDoc] = await Promise.all([
      getDoc(doc(firestore, 'pmm_users', this.session.uid, 'private', 'consent_state')),
      getDoc(doc(firestore, 'pmm_users', this.session.uid, 'private', 'settings'))
    ])
    const consent = consentDoc.exists() ? consentDoc.data() as ConsentState : { version: 0, scopes: {} }
    const settings = settingsDoc.data() ?? {}
    const snapshot: PrivacySnapshot = {
      consent,
      device: {
        trustedDevice: settings.trustedDevice === true,
        privacyMode: settings.privacyMode !== false,
        persistentCacheActive: settings.trustedDevice === true && isConsentGranted(consent, 'offline_persistent_cache')
      }
    }
    this.privacySnapshot = snapshot
    return snapshot
  }

  async acceptCoreService(): Promise<ConsentState> {
    const { functions } = await getPmmFirebaseServices()
    await ensurePmmAppCheckReady()
    const call = httpsCallable<Record<string, never>, ConsentResult>(functions, 'acceptPmmCoreService')
    return (await call({})).data.state
  }

  async recordConsentDecision(input: ConsentDecisionRequest): Promise<ConsentState> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<ConsentDecisionRequest, ConsentResult>(functions, 'recordPmmConsentDecision')
    const state = (await call(input)).data.state
    if (this.privacySnapshot) this.privacySnapshot = { ...this.privacySnapshot, consent: state, device: { ...this.privacySnapshot.device, persistentCacheActive: this.privacySnapshot.device.trustedDevice && isConsentGranted(state, 'offline_persistent_cache') } }
    if (input.scope === 'offline_persistent_cache' && input.decision !== 'grant') await clearEncryptedDeviceVault().catch(() => undefined)
    return state
  }

  async updateDevicePrivacy(settings: Pick<DevicePrivacySettings, 'trustedDevice' | 'privacyMode'>): Promise<DevicePrivacySettings> {
    const { firestore } = await getPmmFirebaseServices()
    await setDoc(doc(firestore, 'pmm_users', this.session.uid, 'private', 'settings'), {
      trustedDevice: settings.trustedDevice,
      privacyMode: settings.privacyMode,
      updatedAt: serverTimestamp(),
      schemaVersion: 'pmm-settings-1'
    }, { merge: true })
    const persistentCacheActive = settings.trustedDevice && Boolean(this.privacySnapshot && isConsentGranted(this.privacySnapshot.consent, 'offline_persistent_cache'))
    const device = { ...settings, persistentCacheActive }
    if (!persistentCacheActive) await clearEncryptedDeviceVault().catch(() => undefined)
    if (this.privacySnapshot) this.privacySnapshot = { ...this.privacySnapshot, device }
    return device
  }

  async requestExport(): Promise<PrivacyJob> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<Record<string, never>, PrivacyJobResult>(functions, 'requestPmmExport')
    return (await call({})).data.job
  }

  async requestDeletion(): Promise<PrivacyJob> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<Record<string, never>, PrivacyJobResult>(functions, 'requestPmmDeletion')
    return (await call({})).data.job
  }

  async clearSensitiveDeviceState(): Promise<void> {
    await clearAllPmmSensitiveDeviceState()
  }

  async syncOfflineQueue(): Promise<OfflineSyncReport> {
    if (!await this.offlineEnabled()) return { synced: 0, pending: 0, conflicts: 0 }
    const queue = await readOfflineQueue(this.session.uid)
    const remaining = []
    let synced = 0
    let conflicts = 0
    for (const item of queue) {
      if (item.state === 'conflict') { remaining.push(item); conflicts += 1; continue }
      try {
        await this.recordDailyInteractionOnline(item.request)
        synced += 1
      } catch (error) {
        if (isOfflineFailure(error)) remaining.push({ ...item, attempts: item.attempts + 1, lastError: safeErrorCode(error) })
        else { remaining.push({ ...item, attempts: item.attempts + 1, state: 'conflict' as const, lastError: safeErrorCode(error) }); conflicts += 1 }
      }
    }
    await replaceOfflineQueue(this.session.uid, remaining)
    return { synced, pending: remaining.filter((item) => item.state === 'pending').length, conflicts }
  }

  async getNotificationSettings(): Promise<NotificationSettingsView> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<Record<string, never>, { settings: NotificationSettingsView }>(functions, 'getPmmNotificationSettings')
    return (await call({})).data.settings
  }

  async updateNotificationSettings(settings: NotificationSettingsUpdate): Promise<NotificationSettingsView> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<NotificationSettingsUpdate, { settings: NotificationSettingsView }>(functions, 'updatePmmNotificationSettings')
    return (await call(settings)).data.settings
  }

  async registerPushInstallation(input: PushInstallationRegistration): Promise<NotificationSettingsView> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<PushInstallationRegistration, { settings: NotificationSettingsView }>(functions, 'registerPmmPushInstallation')
    return (await call(input)).data.settings
  }

  async unregisterPushInstallation(installationId: string): Promise<NotificationSettingsView> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<{ installationId: string }, { settings: NotificationSettingsView }>(functions, 'unregisterPmmPushInstallation')
    return (await call({ installationId })).data.settings
  }

  async sendTestNotification(): Promise<NotificationTestResult> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<Record<string, never>, NotificationTestResult>(functions, 'sendPmmTestNotification')
    return (await call({})).data
  }

  async getOnboardingSession(): Promise<OnboardingSession> {
    const { firestore } = await getPmmFirebaseServices()
    const [sessionDoc, standardDocs, sensitiveDocs] = await Promise.all([
      getDoc(doc(firestore, 'pmm_users', this.session.uid, 'onboarding_sessions', 'onboarding-v1')),
      getDocs(collection(firestore, 'pmm_users', this.session.uid, 'onboarding_responses')),
      getDocs(collection(firestore, 'pmm_users', this.session.uid, 'onboarding_sensitive_responses')),
    ])
    const responses: Record<string, OnboardingResponse> = {}
    for (const responseDoc of [...standardDocs.docs, ...sensitiveDocs.docs]) {
      const data = responseDoc.data() as OnboardingResponse
      responses[data.fieldId] = {
        ...data,
        recordedAt: typeof data.recordedAt === 'string' ? data.recordedAt : new Date().toISOString(),
      }
    }
    if (!sessionDoc.exists()) {
      return {
        sessionId: 'onboarding-v1',
        schemaVersion: ONBOARDING_SCHEMA_VERSION,
        revision: 0,
        currentChapterId: 'orientation',
        completedChapterIds: [],
        state: 'in_progress',
        responses,
        updatedAt: new Date().toISOString(),
      }
    }
    const data = sessionDoc.data() as Omit<OnboardingSession, 'responses'>
    return {
      ...data,
      sessionId: sessionDoc.id,
      responses,
      updatedAt: typeof data.updatedAt === 'string' ? data.updatedAt : new Date().toISOString(),
    }
  }

  async saveOnboardingChapter(request: OnboardingChapterSaveRequest): Promise<OnboardingSession> {
    await ensurePmmAppCheckReady()
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<OnboardingChapterSaveRequest, OnboardingSaveResult>(functions, 'savePmmOnboardingChapter')
    return (await call(request)).data.session
  }

  async submitOnboarding(sessionId: string, expectedRevision: number): Promise<OnboardingSubmissionResult> {
    await ensurePmmAppCheckReady()
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<{ sessionId: string; expectedRevision: number }, OnboardingSubmitResult>(functions, 'submitPmmOnboarding')
    return (await call({ sessionId, expectedRevision })).data
  }

  async restartOnboardingTest(request: OnboardingRestartRequest): Promise<OnboardingRestartResult> {
    await ensurePmmAppCheckReady()
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<OnboardingRestartRequest, OnboardingRestartResult>(functions, 'restartPmmOnboardingTest')
    return (await call(request)).data
  }

  async getRelationshipIntelligence(): Promise<RelationshipIntelligenceBundle> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<Record<string, never>, IntelligenceResult>(functions, 'getOrCreatePmmRelationshipIntelligence')
    return (await call({})).data.bundle
  }

  async correctProfileFact(request: ProfileFactCorrectionRequest): Promise<RelationshipIntelligenceBundle> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<ProfileFactCorrectionRequest, IntelligenceResult>(functions, 'correctPmmProfileFact')
    return (await call(request)).data.bundle
  }

  async reviewSpineHypothesis(request: SpineHypothesisReviewRequest): Promise<RelationshipIntelligenceBundle> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<SpineHypothesisReviewRequest, IntelligenceResult>(functions, 'reviewPmmSpineHypothesis')
    return (await call(request)).data.bundle
  }
}
